/* eslint-disable jsx-a11y/media-has-caption */
import { useVideoBySlug, useVideosByCategory } from '@bpm-web-app/swr-hooks';
import classNames from 'classnames';
import { useRouter } from 'next/router';
import { Analytics, getCurrentPlatformLink } from '@bpm-web-app/utils';
import { useRef } from 'react';
import styles from './video-by-slug.module.css';
import { CardCarousel, useActionModal } from '../../shared';
import { membershipRequiredActionModalProps } from '../../shared/action-modal/action-modal';
import { VideoCard } from '../../shared/card/video-card/video-card';
import { usePlayer } from '../../player-context';

export interface VideoBySlugProps {
    slug: string;
}

export function VideoBySlug({ slug }: VideoBySlugProps) {
    const { data: video, isLoading } = useVideoBySlug(slug);
    const { data: videosForCategory } = useVideosByCategory(video?.category?.slug);
    const { openModal, closeModal: closeActionModal } = useActionModal();
    const { onPause } = usePlayer();

    const router = useRouter();
    const videoRef = useRef<HTMLVideoElement | null>(null);
    if (isLoading) {
        return null;
    }

    if (!video) {
        return <div>Video not found</div>;
    }

    const otherVideos = videosForCategory?.filter((v) => v.id !== video.id) || [];

    return (
        <div className={classNames(styles['video-by-slug'], 'spacing__window')}>
            <div className={styles['video-container']}>
                <video
                    ref={videoRef}
                    className={styles['video-player']}
                    src={video.stream_url || video.thumbnail_url}
                    poster={video.thumbnail_url}
                    onClick={() => {
                        if (!video.stream_url) {
                            videoRef.current?.pause();
                            openModal(
                                membershipRequiredActionModalProps({
                                    isCreate: false,
                                    onConfirm: () => {
                                        router.replace(getCurrentPlatformLink('/account/plan'));
                                        closeActionModal();
                                    },
                                    onClose: () => {
                                        closeActionModal();
                                    },
                                })
                            );
                        }
                    }}
                    onPlay={() => {
                        if (!video.stream_url) {
                            videoRef.current?.pause();
                            openModal(
                                membershipRequiredActionModalProps({
                                    isCreate: false,
                                    onConfirm: () => {
                                        router.replace(getCurrentPlatformLink('/account/plan'));
                                        closeActionModal();
                                    },
                                    onClose: () => {
                                        closeActionModal();
                                    },
                                })
                            );
                        } else {
                            onPause();
                            Analytics.trackClick('play_video', video.id.toString(), { video: video.title });
                            Analytics.trackGeneric('play_video', { video_id: video.id.toString() });
                        }
                    }}
                    onEnded={() => {
                        Analytics.trackGeneric('play_video_ended', { video_id: video.id.toString() });
                    }}
                    preload={video.stream_url ? 'auto' : 'none'}
                    controls
                    controlsList="nodownload"
                />
            </div>
            <h1 className={styles['video-title']}>{video.title}</h1>
            <p className={styles['video-description']}>{video.description}</p>

            {otherVideos.length > 0 && (
                <CardCarousel title={`More in ${video.category?.name}`} isInContainer noPadding>
                    {otherVideos.map((set) => (
                        <VideoCard
                            key={set.id}
                            video={set} />
                    ))}
                </CardCarousel>
            )}
        </div>
    );
}

export default VideoBySlug;
/* eslint-enable jsx-a11y/media-has-caption */
